import { Socket } from 'socket.io-client';
import {
  TaskAssigneeWebsocketEventType,
  TaskBoardWebsocketEventType,
  TaskLabelWebsocketEventType,
  TaskListWebsocketEventType,
  TaskReminderWebsocketEventType,
  TaskWebsocketEventType,
  WorkspaceLabelWebsocketEventType,
  WorkspaceMemberWebsocketEventType,
} from '@nx-frontend/features/web-editor/types/tasks/tasks-websocket';

const compose = (
  f: (params: { eventName: string; payload: unknown }) => void,
  extractVM: (eventSource: GenericWebPlatformEventDTO) => { eventName: string; payload: unknown }
) => {
  return (eventSource: GenericWebPlatformEventDTO) => {
    return f(extractVM(eventSource));
  };
};

interface GenericWebPlatformEventDTO {
  message: {
    before: unknown;
    after: unknown;
    isCreate?: boolean;
    userId: number;
    workspaceId: string;
    globalId: string;
  };
  userId: number;
  workspaceId: string;
}

interface ExtractResult {
  eventName: string;
  payload: GenericWebPlatformEventDTO;
}

export class TasksSocketService {
  constructor(private socketIO: Socket) {}

  static inited = false;

  static init(socketIO: Socket) {
    if (!TasksSocketService.inited) {
      new TasksSocketService(socketIO).listen();
      TasksSocketService.inited = true;
    }
  }

  listen() {
    this.socketIO.on(TaskListWebsocketEventType.CREATE, this.noteTaskListCreateListener);
    this.socketIO.on(TaskListWebsocketEventType.UPDATE, this.noteTaskListUpdateListener);
    this.socketIO.on(TaskListWebsocketEventType.REMOVE, this.noteTaskListRemoveListener);
    this.socketIO.on(TaskListWebsocketEventType.RESTORE, this.noteTaskListRestoreListener);
    this.socketIO.on(TaskWebsocketEventType.CREATE, this.noteTaskCreateListener);
    this.socketIO.on(TaskWebsocketEventType.UPDATE, this.noteTaskUpdateListener);
    this.socketIO.on(TaskWebsocketEventType.REMOVE, this.noteTaskRemoveListener);
    this.socketIO.on(TaskAssigneeWebsocketEventType.CREATE, this.noteTaskAssigneeCreateListener);
    this.socketIO.on(TaskAssigneeWebsocketEventType.REMOVE, this.noteTaskAssigneeRemoveListener);
    this.socketIO.on(TaskLabelWebsocketEventType.CREATE, this.noteTaskLabelCreateListener);
    this.socketIO.on(TaskLabelWebsocketEventType.REMOVE, this.noteTaskLabelRemoveListener);
    this.socketIO.on(WorkspaceLabelWebsocketEventType.CREATE, this.workspaceLabelCreateListener);
    this.socketIO.on(WorkspaceLabelWebsocketEventType.UPDATE, this.workspaceLabelUpdateListener);
    this.socketIO.on(WorkspaceLabelWebsocketEventType.REMOVE, this.workspaceLabelRemoveListener);
    this.socketIO.on(TaskReminderWebsocketEventType.CREATE, this.noteTaskReminderCreateListener);
    this.socketIO.on(TaskReminderWebsocketEventType.UPDATE, this.noteTaskReminderUpdateListener);
    this.socketIO.on(TaskReminderWebsocketEventType.REMOVE, this.noteTaskReminderRemoveListener);
    this.socketIO.on(WorkspaceMemberWebsocketEventType.UPDATE, this.workspaceMemberUpdateListener);
    this.socketIO.on(WorkspaceMemberWebsocketEventType.REMOVE, this.workspaceMemberRemoveListener);
    this.socketIO.on(TaskBoardWebsocketEventType.CHANGE, this.noteTaskBoardChangeListener);
  }

  extractNoteTaskListCreateVM = (eventSource: GenericWebPlatformEventDTO): ExtractResult => {
    return {
      eventName: TaskListWebsocketEventType.CREATE,
      payload: eventSource,
    };
  };

  extractNoteTaskListUpdateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskListWebsocketEventType.UPDATE,
      payload: eventSource,
    };
  };

  extractNoteTaskListRemoveVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskListWebsocketEventType.REMOVE,
      payload: eventSource,
    };
  };

  extractNoteTaskListRestoreVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskListWebsocketEventType.RESTORE,
      payload: eventSource,
    };
  };

  extractNoteTaskCreateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskWebsocketEventType.CREATE,
      payload: eventSource,
    };
  };

  extractNoteTaskUpdateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskWebsocketEventType.UPDATE,
      payload: eventSource,
    };
  };

  extractNoteTaskRemoveVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskWebsocketEventType.REMOVE,
      payload: eventSource,
    };
  };

  extractNoteTaskAssigneeCreateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskAssigneeWebsocketEventType.CREATE,
      payload: eventSource,
    };
  };

  extractNoteTaskAssigneeRemoveVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskAssigneeWebsocketEventType.REMOVE,
      payload: eventSource,
    };
  };

  extractNoteTaskLabelCreateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskLabelWebsocketEventType.CREATE,
      payload: eventSource,
    };
  };

  extractNoteTaskLabelRemoveVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskLabelWebsocketEventType.REMOVE,
      payload: eventSource,
    };
  };

  extractWorkspaceLabelCreateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: WorkspaceLabelWebsocketEventType.CREATE,
      payload: eventSource,
    };
  };

  extractWorkspaceLabelUpdateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: WorkspaceLabelWebsocketEventType.UPDATE,
      payload: eventSource,
    };
  };

  extractWorkspaceLabelRemoveVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: WorkspaceLabelWebsocketEventType.REMOVE,
      payload: eventSource,
    };
  };

  extractNoteTaskReminderCreateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskReminderWebsocketEventType.CREATE,
      payload: eventSource,
    };
  };

  extractNoteTaskReminderUpdateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskReminderWebsocketEventType.UPDATE,
      payload: eventSource,
    };
  };

  extractNoteTaskReminderRemoveVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskReminderWebsocketEventType.REMOVE,
      payload: eventSource,
    };
  };

  extractWorkspaceMemberUpdateVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: WorkspaceMemberWebsocketEventType.UPDATE,
      payload: eventSource,
    };
  };

  extractWorkspaceMemberRemoveVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: WorkspaceMemberWebsocketEventType.REMOVE,
      payload: eventSource,
    };
  };

  extractNoteTaskBoardChangeVM = (eventSource: GenericWebPlatformEventDTO) => {
    return {
      eventName: TaskBoardWebsocketEventType.CHANGE,
      payload: eventSource,
    };
  };

  private sendFeatureSocketEvent = (incomingEventVM: { eventName: string; payload: unknown }) => {
    const windowCommon = window.__NIMBUS_COMMON;
    setTimeout(async () => {
      windowCommon.socketEventsSubj$.next(incomingEventVM);
    });
  };

  noteTaskListCreateListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskListCreateVM);

  noteTaskListUpdateListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskListUpdateVM);

  noteTaskListRemoveListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskListRemoveVM);

  noteTaskListRestoreListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskListRestoreVM);

  noteTaskCreateListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskCreateVM);

  noteTaskUpdateListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskUpdateVM);

  noteTaskRemoveListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskRemoveVM);

  noteTaskAssigneeCreateListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskAssigneeCreateVM);

  noteTaskAssigneeRemoveListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskAssigneeRemoveVM);

  noteTaskLabelCreateListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskLabelCreateVM);

  noteTaskLabelRemoveListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskLabelRemoveVM);

  workspaceLabelCreateListener = compose(this.sendFeatureSocketEvent, this.extractWorkspaceLabelCreateVM);

  workspaceLabelUpdateListener = compose(this.sendFeatureSocketEvent, this.extractWorkspaceLabelUpdateVM);

  workspaceLabelRemoveListener = compose(this.sendFeatureSocketEvent, this.extractWorkspaceLabelRemoveVM);

  noteTaskReminderCreateListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskReminderCreateVM);

  noteTaskReminderUpdateListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskReminderUpdateVM);

  noteTaskReminderRemoveListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskReminderRemoveVM);

  workspaceMemberUpdateListener = compose(this.sendFeatureSocketEvent, this.extractWorkspaceMemberUpdateVM);

  workspaceMemberRemoveListener = compose(this.sendFeatureSocketEvent, this.extractWorkspaceMemberRemoveVM);

  noteTaskBoardChangeListener = compose(this.sendFeatureSocketEvent, this.extractNoteTaskBoardChangeVM);
}
